import {Edit} from "@mui/icons-material";
import {useQuery} from "@tanstack/react-query";
import React, {useEffect, useRef, useState} from 'react';
import {AjaxError} from "rxjs/ajax";
import {DataTypeApiClient} from '../../../../common/apis/datatype/DataTypeApiClient';
import {DataTypeConstraintCreateDto} from "../../../../common/apis/datatype/DataTypeConstraintDto";
import {ValidationError} from "../../../../common/ValidationError";
import Dialog from '../../../../components/dialogs/Dialog';
import DialogContent from '../../../../components/dialogs/DialogContent';
import DialogTitle from '../../../../components/dialogs/DialogTitle';
import Grid from '../../../../components/dialogs/Grid';
import TextField from '../../../../components/fields/textfield/TextField';
import SectionActionBar from "../../../../components/section/SectionActionBar";
import {SectionActionButton} from '../../../../components/section/SectionActionButton';
import SectionHeader from "../../../../components/section/SectionHeader";
import {t} from '../../../../store/localization/TranslMessasge';
import {CommonTranslationKey} from '../CommonTranslationKey';
import {ErrorTranslationKey} from "../ErrorTranslationKey";
import Snackbar from "../snackbar/Snackbar";
import {ConstraintDataPluginRegistry} from "./constraint/ConstraintDataPluginRegistry";
import {DataTypeConstraintCreateDialog} from "./constraint/DataTypeConstraintCreateDialog";
import DataTypeConstraintDeleteDialog from "./constraint/DataTypeConstraintDeleteDialog";
import DataTypeConstraintsGrid from './constraint/DataTypeConstraintsGrid';
import {CoreDataTypeFormatter} from "./CoreDataTypeFormatter";
import {DataTypeTranslationKey} from './DataTypeTranslationKey';
import {DataTypeUpdateDialog} from "./DataTypeUpdateDialog";

interface DataTypeDetailDialogProps {
    open: boolean;
    dataTypeDefinitionId: string;
    onClose: (updated: boolean) => void;
}

export function DataTypeDetailDialog({ open, dataTypeDefinitionId, onClose }: Readonly<DataTypeDetailDialogProps>) {
    const dataTypeApiClient = useRef(new DataTypeApiClient());

    const [dataTypeUpdateDialogOpen, setDataTypeUpdateDialogOpen] = useState(false);
    const [constraintCreateDialogOpen, setConstraintCreateDialogOpen] = useState(false);
    const [constraintDeleteDialogOpen, setConstraintDeleteDialogOpen] = useState(false);
    const [constraintsVisible, setConstraintsVisible] = useState<boolean>(true);
    const [selectedConstraintId, setSelectedConstraintId] = useState<string | undefined>(undefined);
    const [updated, setUpdated] = useState(false);

    const {data: dataType, isError, error, refetch} = useQuery({
        queryKey: ["dataType.dataTypes", dataTypeDefinitionId],
        queryFn: () => dataTypeApiClient.current.findById(dataTypeDefinitionId),
    });

    useEffect(() => {
        if (dataType) {
            const availableTypes = ConstraintDataPluginRegistry.getAvailableTypesByDataType(dataType.coreDataType);
            setConstraintsVisible(availableTypes.length > 0);
        }
    }, [dataType]);

    useEffect(() => {
        if (isError && error) {
            Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error);
        }
    }, [isError, error]);

    function refresh() {
        setUpdated(true);
        refetch();
    }

    function openDataTypeUpdateDialog() {
        setDataTypeUpdateDialogOpen(true);
    }

    function closeDataTypeUpdateDialog() {
        setDataTypeUpdateDialogOpen(false);
    }

    function openConstraintCreateDialog() {
        setConstraintCreateDialogOpen(true);
    }

    function closeConstraintCreateDialog(created: boolean) {
        setConstraintCreateDialogOpen(false);
        if (created) {
            refresh();
        }
    }

    function openConstraintDeleteDialog(constraintId: string) {
        setSelectedConstraintId(constraintId);
        setConstraintDeleteDialogOpen(true);
    }

    function closeConstraintDeleteDialog() {
        setConstraintDeleteDialogOpen(false);
    }

    async function createConstraint(constraint: DataTypeConstraintCreateDto): Promise<void> {
        return new Promise((resolve, reject) => {
            dataTypeApiClient.current.createConstraint(dataTypeDefinitionId, constraint)
                .then(() => {
                    resolve();
                })
                .catch((error: AjaxError | ValidationError) => {
                    reject(error);
                });
        });
    }

    if (!dataType) {
        return null;
    }

    return (
        <>
            <Dialog open={open} onClose={() => onClose(updated)} maxWidth="md">
                <DialogTitle title={t(DataTypeTranslationKey.DATA_TYPE)}
                        onDialogClosed={() => onClose(updated)} />
                <DialogContent>
                    <Grid container>
                        <Grid item xs={12}>
                            <SectionHeader title={t(DataTypeTranslationKey.DETAILS)}>
                                <SectionActionBar>
                                    <SectionActionButton
                                            icon={<Edit fontSize={"inherit"}/>}
                                            tooltip={t(CommonTranslationKey.EDIT)}
                                            onClick={openDataTypeUpdateDialog}
                                            visible={dataType.acl?.canUpdate}/>
                                </SectionActionBar>
                            </SectionHeader>
                        </Grid>
                        <Grid item xs={12} sm={7}>
                            <TextField
                                    label={t(DataTypeTranslationKey.NAME)}
                                    value={dataType.name}
                                    readOnly={true}
                                    fullWidth/>
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextField
                                    label={t(DataTypeTranslationKey.CORE_DATA_TYPE)}
                                    value={CoreDataTypeFormatter.formatCoreDataType(dataType.coreDataType, t)}
                                    readOnly={true}
                                    fullWidth/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                    label={t(CommonTranslationKey.DESCRIPTION)}
                                    value={dataType.description ?? ''}
                                    readOnly={true}
                                    multiline
                                    rows={4}
                                    fullWidth/>
                        </Grid>
                        {constraintsVisible &&
                                <>
                                    <Grid item xs={12}>
                                        <SectionHeader title={t(DataTypeTranslationKey.CONSTRAINTS)}/>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <DataTypeConstraintsGrid
                                                constraints={dataType.constraints ?? []}
                                                createEnabled={dataType.acl?.canCreateConstraints ?? false}
                                                onCreateClicked={openConstraintCreateDialog}
                                                onDeleteClicked={openConstraintDeleteDialog}/>
                                    </Grid>
                                </>
                        }
                    </Grid>
                </DialogContent>
            </Dialog>

            <DataTypeUpdateDialog open={dataTypeUpdateDialogOpen}
                    dataType={dataType}
                    onClose={closeDataTypeUpdateDialog}
                    onUpdated={refresh} />

            <DataTypeConstraintCreateDialog open={constraintCreateDialogOpen}
                    coreDataType={dataType.coreDataType}
                    onCreateConstraint={createConstraint}
                    onClose={closeConstraintCreateDialog} />
            
            <DataTypeConstraintDeleteDialog open={constraintDeleteDialogOpen}
                    dataTypeDefinitionId={dataType.id}
                    constraintId={selectedConstraintId}
                    onClose={closeConstraintDeleteDialog}
                    onDeleted={refresh} />
        </>
    );
}
